import { QueryErrorBoundary } from "@components/common/errors";
import { TableSkeleton } from "@components/common/skeletons";
import { cn } from "@lib/utils/cn";
import type { ColumnDef, Header, Row, Table as TanStackTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { SearchSlash } from "lucide-react";
import React from "react";
import { DataTableMobileCard } from "./DataTableMobileCard";
import { DataTablePagination } from "./DataTablePagination";

/**
 * ## row.isMutating convention (for future adoption)
 *
 * When a row-level mutation is in flight, each Tab can track per-row loading state
 * using a local `useState<Record<string, boolean>>` keyed by row id.
 *
 * - `row.isMutating = true` → dim the row (opacity) + show a spinner in the action cell.
 * - This is **distinct** from the table-wide `loading` / `isFetching` props which
 *   control skeleton/freeze states for the entire dataset.
 * - Adoption happens at Tab migration time — do not retrofit existing Tabs now.
 */
interface DataTableProps<TData> {
  table: TanStackTable<TData>;
  columns: ColumnDef<TData>[];
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  loading?: boolean;
  error?: Error | null;
  onRefetch?: () => void;
  dense?: boolean;
  className?: string;
  renderEmpty?: () => React.ReactNode;
  pinColumns?: string[];
  enableColumnResizing?: boolean;
  mobileCard?: boolean | { renderCard: (row: Row<TData>) => React.ReactNode };
  onRowClick?: (row: Row<TData>, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Slot rendered below the table when rows are selected (e.g. bulk delete, bulk export). */
  bulkActions?: React.ReactNode;
  /** When true, existing rows are dimmed and frozen (refetch in progress). Does NOT show skeleton. */
  isFetching?: boolean;
  /** Returns a status token string to apply a left-border accent + hover tint on that row. */
  rowStatusAccent?: (row: Row<TData>) => string | undefined;
}

export function DataTable<TData>({
  table,
  columns,
  renderExpandedRow,
  loading = false,
  error = null,
  onRefetch,
  dense = false,
  className,
  renderEmpty,
  pinColumns,
  enableColumnResizing,
  mobileCard,
  onRowClick,
  bulkActions,
  isFetching = false,
  rowStatusAccent,
}: DataTableProps<TData>) {
  // Priority: error > loading > empty > table

  if (error) {
    return (
      <div className={cn("space-y-4", className)}>
        <QueryErrorBoundary
          error={error}
          refetch={onRefetch}
          className=""
          title="Erreur de chargement"
        />
        <DataTablePagination table={table} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={cn("space-y-4", className)}>
        <TableSkeleton rows={10} columns={columns.length} hasHeader className="p-4" />
        <DataTablePagination table={table} />
      </div>
    );
  }

  const hasPinnedColumns = pinColumns && pinColumns.length > 0;
  const renderCard =
    typeof mobileCard === "object" && mobileCard !== null ? mobileCard.renderCard : undefined;

  return (
    <div className={cn("space-y-4", className)}>
      <div
        className={cn(
          "border border-[var(--border-table)] rounded-box overflow-hidden",
          isFetching && !loading && "opacity-60 pointer-events-none",
        )}
      >
        {/* Thin indeterminate progress bar shown during refetch */}
        {isFetching && !loading && (
          <progress className="progress progress-primary w-full h-0.5 rounded-none" />
        )}
        <div className="overflow-x-auto">
          <table
            className={cn(
              "table w-full table-pin-rows",
              dense && "table-xs",
              hasPinnedColumns && "table-pin-cols",
            )}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} style={{ backgroundColor: "var(--surface-table-header)" }}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(enableColumnResizing && "relative")}
                      aria-sort={
                        header.column.getIsSorted() === "asc"
                          ? "ascending"
                          : header.column.getIsSorted() === "desc"
                            ? "descending"
                            : undefined
                      }
                      style={
                        hasPinnedColumns && pinColumns?.includes(header.id)
                          ? {
                              backgroundColor: "var(--surface-table-header)",
                              left: `${header.getStart()}px`,
                            }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {enableColumnResizing && header.column.getCanResize() && (
                        <div
                          className="column-resize-handle"
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          role="separator"
                          aria-label="Redimensionner la colonne"
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const statusToken = rowStatusAccent?.(row);
                  const expandedContent = renderExpandedRow?.(row);
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={cn(
                          "transition-colors duration-200 border-b border-[var(--border-table)]",
                          onRowClick && "cursor-pointer",
                        )}
                        data-status-accent={statusToken || undefined}
                        style={
                          statusToken
                            ? ({
                                "--row-status-bg": `var(--status-${statusToken}-bg)`,
                              } as React.CSSProperties)
                            : undefined
                        }
                        onClick={(e) => onRowClick?.(row, e)}
                        data-state={row.getIsSelected() ? "selected" : undefined}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <td
                            key={cell.id}
                            className={cn(
                              (cell.column.columnDef as { numeric?: boolean }).numeric &&
                                "numeric-cell",
                              hasPinnedColumns &&
                                pinColumns?.includes(cell.column.id) &&
                                "bg-[var(--surface-table-header)]",
                            )}
                            style={{
                              ...(hasPinnedColumns && pinColumns?.includes(cell.column.id)
                                ? { left: `${cell.column.getStart()}px` }
                                : {}),
                              ...(cellIndex === 0 && statusToken
                                ? { borderLeft: `4px solid var(--status-${statusToken})` }
                                : {}),
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                      {expandedContent != null && (
                        <tr>
                          <td colSpan={columns.length} className="p-0">
                            {expandedContent}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="flex flex-col items-center justify-center py-16 text-base-content/50">
                      <SearchSlash className="w-12 h-12 mb-3 opacity-40" />
                      {renderEmpty ? (
                        renderEmpty()
                      ) : (
                        <p className="text-lg font-medium">Aucun résultat.</p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk actions — shown when rows are selected */}
      {bulkActions && table.getSelectedRowModel().rows.length > 0 && <div>{bulkActions}</div>}

      {/* Mobile card view — visible only below lg breakpoint */}
      {mobileCard && (
        <DataTableMobileCard table={table} columns={columns} renderCard={renderCard} />
      )}

      <DataTablePagination table={table} />
    </div>
  );
}
